import type AirDatepicker from 'air-datepicker'

/**
 * AirDatepicker positions its popup absolutely against the field once, on open,
 * and only re-positions on window `resize` — never on scroll of an ancestor
 * (a tall form, a drawer body, …). So when the user scrolls, the field moves but
 * the popup stays put and visually detaches from it.
 *
 * We close the popup on any ancestor scroll. `scroll` doesn't bubble, so we listen
 * on the capture phase to catch scrolling of any scroll container. Scrolling that
 * happens *inside* the calendar itself (timepicker track, year list) is ignored so
 * it doesn't dismiss the very popup the user is interacting with.
 *
 * Returns a cleanup function that removes the listener.
 */
export const bindCloseDatepickerOnScroll = (
    getDatepicker: () => AirDatepicker | null,
): (() => void) => {
    const onScroll = (e: Event) => {
        const dp = getDatepicker()
        if (!dp?.visible) return

        const target = e.target as Node | null
        // Don't close on scroll originating within the calendar popup.
        if (target instanceof Element && target.closest('.air-datepicker')) return

        dp.hide()
    }

    window.addEventListener('scroll', onScroll, true)
    return () => window.removeEventListener('scroll', onScroll, true)
}
